import { useState } from "react";
import { sendRecoveryCode, confirmForgotPassword } from "../services/cognito";

export type RecoveryStep = "SEND_CODE" | "CONFIRM_RESET";

const initialForm = {
  email: "",
  code: "",
  newPassword: "",
  confirmPassword: "",
};

export function usePasswordRecovery() {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState<RecoveryStep>("SEND_CODE");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const updateField = (field: keyof typeof initialForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetState = () => {
    setStep("SEND_CODE");
    setForm(initialForm);
    setError("");
    setSuccessMessage("");
    setIsLoading(false);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.email.trim()) {
      setError("Por favor, ingresá tu correo electrónico.");
      return;
    }
    setIsLoading(true);
    try {
      await sendRecoveryCode(form.email.trim());
      setStep("CONFIRM_RESET");
    } catch (err: any) {
      setError(err.message || "Error al enviar el código de recuperación.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent, onSuccess?: () => void) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.code.trim()) {
      setError("Por favor, ingresá el código de verificación.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setIsLoading(true);
    try {
      await confirmForgotPassword(form.email.trim(), form.code.trim(), form.newPassword);
      setSuccessMessage("Contraseña restablecida con éxito.");
      setTimeout(() => {
        resetState();
        if (onSuccess) onSuccess();
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Error al restablecer la contraseña.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    step,
    form,
    updateField,
    email: form.email,
    code: form.code,
    newPassword: form.newPassword,
    confirmPassword: form.confirmPassword,
    error,
    successMessage,
    isLoading,
    handleSendCode,
    handleConfirmReset,
    resetState,
    setError,
  };
}
